export const THUMB_BG = "#eee5d9";

export function thumbnailUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/thumbs/")) return url;
    parsed.pathname = `/thumbs/${parsed.pathname.replace(/^\//, "").replace(/\.(png|jpe?g)$/i, ".webp")}`;
    return parsed.toString();
  } catch {
    return url;
  }
}

export function originalImageUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith("/thumbs/") && /\.webp$/i.test(parsed.pathname)) {
      parsed.pathname = parsed.pathname.slice("/thumbs".length).replace(/\.webp$/i, ".png");
    }
    return parsed.origin === window.location.origin && String(url).startsWith("/") ? `${parsed.pathname}${parsed.search}` : parsed.toString();
  } catch {
    return url;
  }
}

export function imageProxyUrl(url) {
  if (!url) return "";
  if (url.startsWith("/")) return url;
  return `/api/image?url=${encodeURIComponent(url)}`;
}

async function pngBlob(url) {
  const sources = [...new Set([originalImageUrl(url), url].filter(Boolean))];
  let blob;
  for (const source of sources) {
    const response = await fetch(imageProxyUrl(source), { cache: "force-cache" }).catch(() => null);
    if (!response?.ok || response.type === "opaque") continue;
    const candidate = await response.blob();
    if (candidate.size) { blob = candidate; break; }
  }
  if (!blob) throw new Error("Não foi possível carregar a imagem.");
  if (!blob.size) throw new Error("A imagem está vazia.");
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) => canvas.toBlob((png) => png ? resolve(png) : reject(new Error("Não foi possível converter a imagem.")), "image/png"));
}

export async function copySticker(url) {
  if (!navigator.clipboard || !window.ClipboardItem) return false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob(url) })]);
    return true;
  } catch {
    return false;
  }
}

export async function downloadSticker(url, filename = "figurinha.png") {
  try {
    const blob = await pngBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    return true;
  } catch {
    return false;
  }
}

export function subscriptionState(profile) {
  if (!profile) return { status: "unknown", blocked: false, days: null };
  if (profile.ativo === false) return { status: "expired", blocked: true, days: 0 };
  if (profile.plano === "vitalicio") return { status: "lifetime", blocked: false, days: null };
  const expires = profile.expira_em ? new Date(profile.expira_em) : null;
  const lifetime = expires && !Number.isNaN(expires.getTime()) && expires.getUTCFullYear() >= 2090;
  if (lifetime) return { status: "lifetime", blocked: false, days: null };
  if (!expires || expires.getTime() < Date.now()) {
    return { status: "expired", blocked: true, days: 0 };
  }
  return {
    status: "active",
    blocked: false,
    days: Math.max(0, Math.ceil((expires.getTime() - Date.now()) / 86400000)),
  };
}

export const PLAN_FEATURES = [
  { id: "acervo", label: "Acervo de figurinhas", description: "Categorias, temas e galerias comuns" },
  { id: "favoritos", label: "Favoritos", description: "Salvar figurinhas preferidas" },
  { id: "tutoriais", label: "Tutoriais", description: "Vídeos de uso e instalação" },
  { id: "sombras_molduras", label: "Sombras & Molduras", description: "Produto visual com 412 itens" },
  { id: "fornecedores", label: "Lista de fornecedores", description: "Diretório especial de fornecedores" },
];

export const DEFAULT_PLAN_FEATURES = {
  trimestral: { acervo: true, favoritos: true, tutoriais: true, sombras_molduras: false, fornecedores: false },
  vitalicio: { acervo: true, favoritos: true, tutoriais: true, sombras_molduras: true, fornecedores: true },
};

export function normalizePlanFeatures(value = {}) {
  return Object.fromEntries(Object.entries(DEFAULT_PLAN_FEATURES).map(([plan, defaults]) => [plan, { ...defaults, ...(value?.[plan] || {}) }]));
}

export function hasPlanFeature(profile, settings, feature) {
  if (profile?.is_admin) return true;
  const state = subscriptionState(profile);
  if (state.status === "unknown" || state.blocked) return false;
  if (profile?.acessos && typeof profile.acessos === "object" && Object.prototype.hasOwnProperty.call(profile.acessos, feature)) {
    return profile.acessos[feature] !== false;
  }
  if (feature === "fornecedores" && profile?.tem_fornecedores) return true;
  const plan = state.status === "lifetime" ? "vitalicio" : profile?.plano === "vitalicio" ? "vitalicio" : "trimestral";
  return normalizePlanFeatures(settings?.plan_features)[plan]?.[feature] !== false;
}
